import type * as zod from 'zod';

import * as log from '@sb/log';
import type {
  DeviceKinematics,
  MotionPlannerInterface,
} from '@sb/motion-planning';
import { EventEmitter } from '@sb/utilities';
import type { EventData } from '@sb/utilities';

import type { DeviceFeature } from './DeviceFeature';
import type {
  DeviceCommandResult,
  DeviceCommand,
  DeviceConfiguration,
  DeviceKind,
  EquipmentItem,
} from './deviceZodTypes';

type DeviceEvents<ReasonKind = string> = {
  destroy: ReasonKind;
};

const ns = log.namespace('integrations.types.Device');

/**
 * A Device is the botman-side implementation of equipment that we support.
 * It can be found in the `implementation` directory in each integration.
 */
export abstract class Device<
  State extends object,
  Events = {},
> extends EventEmitter<Events & DeviceEvents> {
  protected features: Map<string, DeviceFeature> = new Map();

  public abstract readonly stateSchema: zod.ZodSchema<
    State,
    zod.ZodTypeDef,
    unknown
  >;

  protected motionPlanner!: MotionPlannerInterface;

  public setMotionPlanner(motionPlanner: MotionPlannerInterface) {
    this.motionPlanner = motionPlanner;
  }

  public abstract readonly kinematics: DeviceKinematics<DeviceCommand>;

  public abstract readonly kind: DeviceKind;

  public abstract executeCommand(
    command: DeviceCommand,
  ): Promise<DeviceCommandResult>;

  public abstract initialize(): Promise<void> | void;

  public destroy(
    ...reason: EventData<(Events & DeviceEvents)['destroy']>
  ): void {
    for (const feature of this.features.values()) {
      feature
        .destroy()
        .catch((err) => log.error(ns`destroy.feature.error`, err));
    }

    this.features.clear();

    this.emit('destroy', ...reason);
  }

  public abstract stop(): Promise<void> | void | undefined;

  public abstract update(): State;

  public onUpdateEvent(_: EquipmentItem): void {}

  /**
   * Return the device to its safe default, dropping any operator opt-in that
   * should not survive a failure. Called when the robot leaves normal running for
   * any reason — E-Stop, failure, or a control-system fault.
   *
   * Devices whose safe state is simply "not moving" need nothing here; `stop()`
   * already covers them. Override only when the device holds *armed* state that
   * outlives a stop, such as the Miller welder's live-weld mode.
   *
   * Implementations must be safe to call repeatedly, from any device state, and
   * must not throw — the caller resets every device and cannot let one failure
   * strand the others.
   */
  public resetToSafeState(): Promise<void> | void {}

  public untilDestroyed() {
    return this.next('destroy');
  }

  public checkDeviceConnectedAtInitialization(): Promise<void> | void {}

  protected abstract readonly config: DeviceConfiguration;

  public getConfig(): DeviceConfiguration {
    return this.config;
  }

  protected async addFeature(feature: DeviceFeature): Promise<void> {
    if (this.features.has(feature.name)) {
      throw new Error(`Feature ${feature.name} già presente`);
    }

    await feature.initialize();
    this.features.set(feature.name, feature);
  }

  protected async removeFeature(name: string): Promise<void> {
    const feature = this.features.get(name);

    if (feature) {
      await feature.destroy();
      this.features.delete(name);
    }
  }

  public getFeature<T extends DeviceFeature>(name: string): T | undefined {
    return this.features.get(name) as T | undefined;
  }
}
